import { Headline3, Card, FlexColSm, FlexSm } from 'components/Elements';
import TextAreaField from 'components/forms/TextAreaField';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import React, { useEffect } from 'react';
import {
    FieldArrayWithId,
    FieldErrors,
    UseFormRegister,
    UseFieldArrayAppend,
    UseFieldArrayRemove,
    UseFormWatch,
} from 'react-hook-form';
import { requiredFieldErrorMessage, createErrorMessage, fieldErrorCodes } from 'shared/error-messages';
import { ImageCard } from '../../FileCard';
import {
    AddImageContent,
    AddTextContent,
    EditImageContent,
    EditTextContent,
    ModalState,
    ResourcesInput,
    TextImageTaskInput,
} from './SharedActivityFormComponents';
import strings from 'strings/strings.json';
import Button from 'components/buttons/Button';
import { LearningResourcesFormModal } from '../../LearningResourcesFormModal';
import SelectImageModal from '../../SelectImageModal';
import { ResourcesContainer } from 'pages/build-course/shared-styles/courseBuilderFormComponents';
import ImageIcon from 'assets/icons/pathway/ImageIcon';
import { theme } from 'theme';
import { IconSize } from 'assets/icons/icon-sizes';

type TextAndImagesProps = {
    fields: FieldArrayWithId<TextImageTaskInput, 'contents', 'id'>[];
    errors: FieldErrors<TextImageTaskInput>;
    register: UseFormRegister<TextImageTaskInput>;
    append: UseFieldArrayAppend<TextImageTaskInput, 'contents'>;
    remove: UseFieldArrayRemove;

    addTextButtonRef: React.RefObject<HTMLButtonElement>;
    contentRequiredError: boolean;
    setContentRequiredError: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenImageModal: () => void;
    disable: boolean;
    watch: UseFormWatch<TextImageTaskInput>;
};

export function TextAndImages({
    fields,
    errors,
    register,
    append,
    remove,
    addTextButtonRef,
    contentRequiredError,
    setContentRequiredError,
    setOpenImageModal,
    disable,
    watch,
}: TextAndImagesProps) {
    const textMaxLength = 2000;
    const imageDescriptionMaxLength = 2000;

    const addImageButtonRef = React.useRef<HTMLButtonElement>(null);
    const watchedContents = watch('contents');

    useEffect(() => {
        if (watchedContents.length > 0) {
            setContentRequiredError(false);
        }
    }, [watchedContents]); //eslint-disable-line

    return (
        <>
            <Headline3>{strings.textAndImagesCard.headline}</Headline3>
            <ResourcesContainer>
                {fields.map((field, i) => {
                    if (field.type === 'editText') {
                        const watchedContent = watchedContents[i] as EditTextContent;
                        return (
                            <React.Fragment key={i}>
                                <Card>
                                    <FlexColSm>
                                        <TextAreaField
                                            height={200}
                                            charactersLeft={textMaxLength - watchedContent.text.length}
                                            maxLength={textMaxLength}
                                            required
                                            fieldName={strings.taskContentForm.contentLabel}
                                            aria={strings.taskContentForm.contentAria}
                                            errorMessage={errors.contents?.[i]?.message}
                                            {...register(`contents.${i}.text`, {
                                                required: {
                                                    value: true,
                                                    message: requiredFieldErrorMessage(
                                                        strings.taskContentForm.contentLabel,
                                                    ),
                                                },
                                                maxLength: {
                                                    value: textMaxLength,
                                                    message: createErrorMessage(
                                                        strings.taskContentForm.contentLabel,
                                                        fieldErrorCodes.maxLength,
                                                    ),
                                                },
                                            })}
                                        />
                                        <Button
                                            title={strings.textAndImagesCard.removeTextButton}
                                            aria={strings.textAndImagesCard.removeTextButton}
                                            colour="BLACK"
                                            onClick={() => {
                                                remove(i);
                                                addTextButtonRef.current?.focus(); //not working
                                            }}
                                            disabled={disable}
                                        />
                                    </FlexColSm>
                                </Card>
                            </React.Fragment>
                        );
                    } else if (field.type === 'addText') {
                        const watchedContent = watchedContents[i] as AddTextContent;
                        return (
                            <React.Fragment key={i}>
                                <Card>
                                    <FlexColSm>
                                        <TextAreaField
                                            height={200}
                                            charactersLeft={textMaxLength - watchedContent.text.length}
                                            maxLength={textMaxLength}
                                            required
                                            fieldName={strings.taskContentForm.contentLabel}
                                            aria={strings.taskContentForm.contentAria}
                                            errorMessage={errors.contents?.[i]?.message}
                                            {...register(`contents.${i}.text`, {
                                                required: {
                                                    value: true,
                                                    message: requiredFieldErrorMessage(
                                                        strings.taskContentForm.contentLabel,
                                                    ),
                                                },
                                                maxLength: {
                                                    value: textMaxLength,
                                                    message: createErrorMessage(
                                                        strings.taskContentForm.contentLabel,
                                                        fieldErrorCodes.maxLength,
                                                    ),
                                                },
                                            })}
                                        />
                                        <Button
                                            title={strings.textAndImagesCard.removeTextButton}
                                            aria={strings.textAndImagesCard.removeTextButton}
                                            colour="BLACK"
                                            onClick={() => {
                                                remove(i);
                                                addTextButtonRef.current?.focus(); //not working
                                            }}
                                            disabled={disable}
                                        />
                                    </FlexColSm>
                                </Card>
                            </React.Fragment>
                        );
                    } else if (field.type === 'addImage') {
                        const watchedContent = watchedContents[i] as AddImageContent;

                        return (
                            <React.Fragment key={i}>
                                <ImageCard
                                    key={'image_' + i}
                                    imageDescription={field.imageDescription}
                                    filePath={field.previewUrl}
                                    source={'local'}
                                    removeFile={() => remove(i)}
                                    disabled={disable}
                                    removeFileButtonRef={addImageButtonRef}
                                >
                                    <TextAreaField
                                        height={200}
                                        charactersLeft={
                                            imageDescriptionMaxLength - watchedContent.imageDescription.length
                                        }
                                        maxLength={imageDescriptionMaxLength}
                                        required
                                        fieldName={strings.textAndImagesCard.imageDescription}
                                        aria={strings.textAndImagesCard.imageDescription}
                                        errorMessage={errors.contents?.[i]?.message}
                                        {...register(`contents.${i}.imageDescription`, {
                                            required: {
                                                value: true,
                                                message: requiredFieldErrorMessage(
                                                    strings.textAndImagesCard.imageDescription,
                                                ),
                                            },
                                            maxLength: {
                                                value: imageDescriptionMaxLength,
                                                message: createErrorMessage(
                                                    strings.textAndImagesCard.imageDescription,
                                                    fieldErrorCodes.maxLength,
                                                ),
                                            },
                                        })}
                                    />
                                </ImageCard>
                            </React.Fragment>
                        );
                    } else if (field.type === 'editImage') {
                        const watchedContent = watchedContents[i] as EditImageContent;
                        const imageDescription = watchedContent.imageDescription ?? '';
                        return (
                            <React.Fragment key={i}>
                                <ImageCard
                                    key={'image_' + i}
                                    filePath={field.s3Filepath}
                                    imageDescription={field.imageDescription}
                                    source={'provider'}
                                    disabled={disable}
                                    removeFile={() => remove(i)}
                                    removeFileButtonRef={addImageButtonRef}
                                >
                                    <TextAreaField
                                        height={200}
                                        charactersLeft={imageDescriptionMaxLength - imageDescription.length}
                                        maxLength={imageDescriptionMaxLength}
                                        required
                                        fieldName={strings.textAndImagesCard.imageDescription}
                                        aria={strings.textAndImagesCard.imageDescription}
                                        errorMessage={errors.contents?.[i]?.message}
                                        {...register(`contents.${i}.imageDescription`, {
                                            required: {
                                                value: true,
                                                message: requiredFieldErrorMessage(
                                                    strings.textAndImagesCard.imageDescription,
                                                ),
                                            },
                                            maxLength: {
                                                value: imageDescriptionMaxLength,
                                                message: createErrorMessage(
                                                    strings.textAndImagesCard.imageDescription,
                                                    fieldErrorCodes.maxLength,
                                                ),
                                            },
                                        })}
                                    />
                                </ImageCard>
                            </React.Fragment>
                        );
                    } else return <></>;
                })}

                <FlexSm>
                    {fields.filter((x) => x.type === 'editText' || x.type === 'addText').length < 1 && (
                        <Button
                            ref={addTextButtonRef}
                            title={strings.textAndImagesCard.addTextButton}
                            aria={strings.textAndImagesCard.addTextButton}
                            onClick={() => {
                                append({ type: 'addText', text: '' });
                                setContentRequiredError(false);
                            }}
                        />
                    )}
                    {fields.filter((x) => x.type === 'editImage' || x.type === 'addImage').length < 2 && (
                        <Button
                            title={strings.textAndImagesCard.addImageButton}
                            aria={strings.textAndImagesCard.addImageButton}
                            onClick={setOpenImageModal}
                            icon={<ImageIcon colour={theme.primaryBrandColour} size={IconSize.MEDIUM} />}
                        />
                    )}
                </FlexSm>

                {contentRequiredError && (
                    <ErrorNotifierSlim description={strings.textAndImagesCard.contentRequiredError} />
                )}
            </ResourcesContainer>
        </>
    );
}

type ModalsProp = {
    modalState: ModalState;
    closeResourceModal: () => void;
    closeImageModal: () => void;
    addResource: (resources: ResourcesInput) => void;
    setImage: (imageFile: File, imageDescription: string) => void;
};

export function Modals({ modalState, closeResourceModal, closeImageModal, addResource, setImage }: ModalsProp) {
    if (modalState === 'closed') return <></>;
    return (
        <>
            {modalState === 'addResource' && (
                <LearningResourcesFormModal closeModal={closeResourceModal} addResource={addResource} />
            )}
            {modalState === 'addImage' && <SelectImageModal closeModal={closeImageModal} setImage={setImage} />}
        </>
    );
}
